import React from 'react';

import { IonCol, IonGrid, IonRow } from '@ionic/react';

import './Table.css';
import { ReportDataResponseBody } from '../../api/types/generated/report_data_post.response';
import { useUserState } from '../../hooks/context/useUserState';

interface TableProps {
  data: ReportDataResponseBody;
  id: string;
}

type TableCellStyle = {
  fontSize?: string;
  fontWeight?: number;
  fontStyle?: string;
  textAlign?: string;
};

type TableRow = {
  value: string | number;
  width: string;
  style?: TableCellStyle;
  className?: string;
}[];

const getColumnStyle = (data): TableCellStyle => {
  const style: TableCellStyle = {};
  if (data.fontSize) {
    style.fontSize = data.fontSize;
  }
  if (data.fontStyle) {
    if (data.fontStyle === 'bold') {
      style.fontWeight = 700;
    } else if (data.fontStyle === 'italic') {
      style.fontStyle = 'italic';
    }
  }
  if (data.textAlignment) {
    style.textAlign = data.textAlignment;
  }
  return style;
};

export default function Table({ data, id }: TableProps) {
  const {
    state: { language },
  } = useUserState();
  const tableData: TableRow[] = [];
  if (data.table && data.table.rowsHeader) {
    const rowsHeader = data.table.rowsHeader;
    const style = data.table?.columnsHeader
      ? getColumnStyle(data.table.columnsHeader)
      : {};
    const firstColumnHeaderStyle = { ...style };
    if (rowsHeader.textAlignment) {
      firstColumnHeaderStyle.textAlign = rowsHeader.textAlignment;
    }
    const headerRow: TableRow = [
      {
        value: rowsHeader.label || '',
        width: rowsHeader.columnWidth
          ? rowsHeader.columnWidth.toString()
          : 'auto',
        style: firstColumnHeaderStyle,
      },
    ];
    data.datasets.forEach(({ label, columnWidth }) => {
      headerRow.push({
        value: label || '',
        width: columnWidth ? columnWidth.toString() : 'auto',
        style,
      });
    });
    const headerRowNotEmpty = headerRow.find(({ value }) => value !== '');
    if (headerRowNotEmpty) {
      tableData.push(headerRow);
    }
  }
  const formattingStrings: any = {};
  data.datasets.forEach((dataset, datasetIndex) => {
    if (dataset.formattingStrings) {
      formattingStrings[datasetIndex] = Array.from(
        { length: dataset.reportData.length },
        (value, index) =>
          dataset.formattingStrings &&
          dataset.formattingStrings[index % dataset.formattingStrings.length]
      );
    }
  });
  data.datasets[0].reportData.forEach((row, rowIndex) => {
    const rowData: TableRow = row.map((value, index) => {
      let width;
      let style: TableCellStyle = {};
      let className;
      if (index === 0) {
        // Column for headers
        const headerColumnWidth = data.table?.rowsHeader?.columnWidth;
        width = headerColumnWidth ? headerColumnWidth.toString() : 'auto';
        if (data.table?.rowsHeader) {
          style = getColumnStyle(data.table.rowsHeader);
        }
      } else {
        // Column for the first dataset
        const datasetColumnWidth = data.datasets[0].columnWidth;
        if (data.datasets[0].textAlignment) {
          style.textAlign = data.datasets[0].textAlignment;
        }
        width = datasetColumnWidth ? datasetColumnWidth.toString() : 'auto';
        value = value.toLocaleString(language);
        if (formattingStrings[0] && formattingStrings[0][rowIndex]) {
          value = formattingStrings[0][rowIndex].replace('{value}', value);
        }
        className = 'value';
      }

      return {
        value,
        width,
        style,
        className,
      };
    });
    // Columns for all datasets except the first one
    for (let index = 1; index < data.datasets.length; index++) {
      const dataset = data.datasets[index];
      for (
        let index = 1;
        index < dataset.reportData[rowIndex].length;
        index++
      ) {
        let value = dataset.reportData[rowIndex][index];
        value = value.toLocaleString(language);
        if (formattingStrings[index] && formattingStrings[index][rowIndex]) {
          value = formattingStrings[index][rowIndex].replace('{value}', value);
        }
        const style: TableCellStyle = {};
        if (dataset.textAlignment) {
          style.textAlign = dataset.textAlignment;
        }
        const className = 'value';
        rowData.push({
          value,
          width: dataset.columnWidth ? dataset.columnWidth.toString() : 'auto',
          style,
          className,
        });
      }
    }
    tableData.push(rowData);
  });

  return (
    <IonGrid id={`table-${id}`} className="report-table">
      {tableData.map((row, index) => (
        <IonRow key={index}>
          {row.map(({ value, width, style, className }, index) => (
            <IonCol
              key={index}
              size={width}
              style={style || {}}
              {...(className && { className })}
            >
              {value}
            </IonCol>
          ))}
        </IonRow>
      ))}
    </IonGrid>
  );
}
